import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["valueDisplay", "alternateValues", "addButton", "editForm", "alternateDropdown"]
  static values = {
    fieldPath: String,
    fieldType: String,
    enumValues: Array,
    recommendations: Array,
    sourcePlanDesignId: String,
    alternates: Array,  // Array of {id, name, value, displayValue, updateUrl, different}
    originalValue: String,  // Raw value for editing
    originalDisplayValue: String  // Display value for comparison/display
  }

  connect() {
    this.isEditing = false
    this.currentEditingAlternate = null
  }

  // Show the dropdown to select which alternate to edit/add
  showAlternateDropdown(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.hasAlternateDropdownTarget) {
      this.alternateDropdownTarget.classList.toggle("hidden")
    }
  }

  // Hide dropdown when clicking outside
  hideDropdown(event) {
    if (this.hasAlternateDropdownTarget && !this.element.contains(event.target)) {
      this.alternateDropdownTarget.classList.add("hidden")
    }
  }

  // Select an alternate to edit
  selectAlternate(event) {
    event.preventDefault()
    const alternateId = event.currentTarget.dataset.alternateId
    const alternate = this.alternatesValue.find(a => a.id === alternateId)

    if (alternate) {
      this.showEditForm(alternate)
    }

    if (this.hasAlternateDropdownTarget) {
      this.alternateDropdownTarget.classList.add("hidden")
    }
  }

  // Edit an existing alternate value
  editAlternate(event) {
    event.preventDefault()
    event.stopPropagation()
    const alternateId = event.currentTarget.dataset.alternateId
    const alternate = this.alternatesValue.find(a => a.id === alternateId)

    if (alternate) {
      this.showEditForm(alternate)
    }
  }

  // Show inline edit form
  showEditForm(alternate) {
    this.isEditing = true
    this.currentEditingAlternate = alternate

    // Create and show the edit form
    const formHtml = this.buildEditFormHtml(alternate)

    // Insert form after the alternate values display
    if (this.hasEditFormTarget) {
      this.editFormTarget.innerHTML = formHtml
      this.editFormTarget.classList.remove("hidden")
    }

    // Focus the input
    const input = this.editFormTarget.querySelector("input, select")
    if (input) {
      input.focus()
      if (input.select) input.select()
    }
  }

  buildEditFormHtml(alternate) {
    const currentValue = alternate.value || ""
    let inputHtml = ""

    if (this.fieldTypeValue === "enum" && this.enumValuesValue.length > 0) {
      // Enum field - dropdown
      const options = this.enumValuesValue.map(v => {
        const selected = v === currentValue ? "selected" : ""
        return `<option value="${this.escapeHtml(v)}" ${selected}>${this.escapeHtml(v)}</option>`
      }).join("")

      inputHtml = `
        <select data-action="keydown->alternate-field-editor#handleKeydown"
                class="w-full text-sm border border-blue-400 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">-- Select --</option>
          ${options}
        </select>
      `
    } else if (this.recommendationsValue.length > 0) {
      // Text field with recommendations
      const datalistId = `recommendations-${alternate.id}-${this.fieldPathValue.replace(/\./g, "-")}`
      const options = this.recommendationsValue.map(v =>
        `<option value="${this.escapeHtml(v)}">`
      ).join("")

      inputHtml = `
        <input type="text"
               value="${this.escapeHtml(currentValue)}"
               list="${datalistId}"
               data-action="keydown->alternate-field-editor#handleKeydown"
               class="w-full text-sm border border-blue-400 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500">
        <datalist id="${datalistId}">
          ${options}
        </datalist>
      `
    } else {
      // Plain text field
      inputHtml = `
        <input type="text"
               value="${this.escapeHtml(currentValue)}"
               data-action="keydown->alternate-field-editor#handleKeydown"
               class="w-full text-sm border border-blue-400 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500">
      `
    }

    return `
      <div class="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
        <div class="text-xs text-blue-600 font-medium mb-1">${this.escapeHtml(alternate.name)}</div>
        ${inputHtml}
        <div class="flex gap-2 mt-2">
          <button type="button"
                  data-action="click->alternate-field-editor#save"
                  class="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
            Save
          </button>
          <button type="button"
                  data-action="click->alternate-field-editor#cancel"
                  class="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </div>
    `
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.save()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.cancel()
    }
  }

  async save() {
    if (!this.currentEditingAlternate) return

    const input = this.editFormTarget.querySelector("input, select")
    const newValue = input ? input.value : ""

    try {
      const response = await fetch(this.currentEditingAlternate.updateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
        },
        body: JSON.stringify({
          source_plan_design_id: this.sourcePlanDesignIdValue,
          field_path: this.fieldPathValue,
          value: newValue
        })
      })

      const data = await response.json()

      if (data.status === "success") {
        // Update the displayed value using both raw and display values
        this.updateAlternateDisplay(
          this.currentEditingAlternate.id,
          data.new_value,
          data.new_display_value,
          data.is_different
        )
        this.hideEditForm()
      } else {
        alert("Error saving: " + data.message)
      }
    } catch (error) {
      console.error("Error saving alternate field:", error)
      alert("Error saving field. Please try again.")
    }
  }

  cancel() {
    this.hideEditForm()
  }

  hideEditForm() {
    this.isEditing = false
    this.currentEditingAlternate = null

    if (this.hasEditFormTarget) {
      this.editFormTarget.innerHTML = ""
      this.editFormTarget.classList.add("hidden")
    }
  }

  updateAlternateDisplay(alternateId, newValue, newDisplayValue, isDifferent) {
    // Update the alternates value array with both raw and display values
    const alternateIndex = this.alternatesValue.findIndex(a => a.id === alternateId)
    if (alternateIndex >= 0) {
      const updatedAlternates = [...this.alternatesValue]
      updatedAlternates[alternateIndex] = {
        ...updatedAlternates[alternateIndex],
        value: newValue,
        displayValue: newDisplayValue,
        different: isDifferent
      }
      this.alternatesValue = updatedAlternates
    }

    // Find and update the display element
    const displayEl = this.element.querySelector(`[data-alternate-id="${alternateId}"]`)
    if (displayEl) {
      const valueSpan = displayEl.querySelector(".alternate-value")
      if (valueSpan) {
        // Use display value for showing, with appropriate styling for N/A
        const displayText = newDisplayValue || "—"
        if (displayText.toLowerCase() === "n/a") {
          valueSpan.innerHTML = `<span class="text-blue-400">${this.escapeHtml(displayText)}</span>`
        } else {
          valueSpan.textContent = displayText
        }
      }

      // Show/hide based on whether display value differs from original display value
      if (isDifferent) {
        displayEl.classList.remove("hidden")
      } else {
        displayEl.classList.add("hidden")
      }
    }
  }

  // Delete/reset an alternate value to match the original
  async resetToOriginal(event) {
    event.preventDefault()
    event.stopPropagation()

    const alternateId = event.currentTarget.dataset.alternateId
    const alternate = this.alternatesValue.find(a => a.id === alternateId)

    if (!alternate) return

    if (!confirm(`Reset ${alternate.name} value to match the original?`)) return

    try {
      const response = await fetch(alternate.updateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
        },
        body: JSON.stringify({
          source_plan_design_id: this.sourcePlanDesignIdValue,
          field_path: this.fieldPathValue,
          reset_to_original: "true"
        })
      })

      const data = await response.json()

      if (data.status === "success") {
        this.updateAlternateDisplay(
          alternateId,
          data.new_value,
          data.new_display_value,
          data.is_different
        )
      } else {
        alert("Error resetting: " + data.message)
      }
    } catch (error) {
      console.error("Error resetting alternate field:", error)
      alert("Error resetting field. Please try again.")
    }
  }

  escapeHtml(text) {
    if (text === null || text === undefined) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
